import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';

import { useAuth, usePortalSession } from '../auth';
import { signOutPortalGuest } from '../services/portalApi';

export function Header() {
  const {
    isLoading,
    signOut,
  } = useAuth();
  const portalSession = usePortalSession();
  const user = portalSession.user;
  const isSignedIn = portalSession.status === 'ready';

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    function handlePointerDown(event: PointerEvent): void {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    }

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener(
        'pointerdown',
        handlePointerDown,
      );
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMenuOpen]);

  async function handleSignOut(): Promise<void> {
    setIsMenuOpen(false);
    setIsSigningOut(true);

    try {
      if (user?.identityType === 'Guest') {
        await signOutPortalGuest();
        portalSession.refresh();
        window.location.assign('/');
        return;
      }

      await signOut();
    } catch (error) {
      console.error('Portal sign-out failed.', error);
      setIsSigningOut(false);
    }
  }

  const displayName =
    user?.displayName ??
    (portalSession.status === 'loading'
      ? 'Loading profile...'
      : 'Portal account');

  return (
    <header className="top-bar">
      <Link className="brand" to={isSignedIn ? '/portal' : '/'}>
        <span className="brand-mark">
          <img
            src="/uiotransperent.png"
            width="30"
            alt="YWAM Norway"
          />
        </span>

        <span>
          <strong>Phonero Portal</strong>
          <small>YWAM Norway</small>
        </span>
      </Link>

      {isSignedIn ? (
        <div className="user-menu" ref={menuRef}>
          <button
            className="user-menu-trigger"
            type="button"
            aria-haspopup="menu"
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen((current) => !current)}
            disabled={isLoading || isSigningOut}
          >
            <span className="user-menu-avatar" aria-hidden="true">
              {getInitials(user?.displayName)}
            </span>

            <span className="user-menu-trigger-text">
              <strong>{displayName}</strong>
              {user?.organization ? (
                <small>{user.organization}</small>
              ) : null}
            </span>

            <span
              className={
                'user-menu-chevron' +
                (isMenuOpen ? ' user-menu-chevron-open' : '')
              }
              aria-hidden="true"
            >
              ▾
            </span>
          </button>

          {isMenuOpen ? (
            <div className="user-menu-dropdown" role="menu">
              <div className="user-menu-identity">
                <strong>{displayName}</strong>
                {user?.email ? <span>{user.email}</span> : null}
                {user?.isAdministrator ? (
                  <span className="user-menu-role">
                    Administrator
                  </span>
                ) : null}
              </div>

              <div className="user-menu-divider" />

              <Link
                className="user-menu-item"
                to="/profile"
                role="menuitem"
                onClick={() => setIsMenuOpen(false)}
              >
                My profile
              </Link>

              <Link
                className="user-menu-item"
                to="/my-booking"
                role="menuitem"
                onClick={() => setIsMenuOpen(false)}
              >
                My booking
              </Link>

              {user?.isAdministrator ? (
                <Link
                  className="user-menu-item"
                  to="/admin"
                  role="menuitem"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Administration
                </Link>
              ) : null}

              <div className="user-menu-divider" />

              <button
                className="user-menu-item user-menu-sign-out"
                type="button"
                role="menuitem"
                onClick={() => void handleSignOut()}
                disabled={isSigningOut}
              >
                {isSigningOut ? 'Signing out...' : 'Sign out'}
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        <Link className="secondary-button button-link" to="/">
          Sign in
        </Link>
      )}
    </header>
  );
}

function getInitials(displayName?: string): string {
  if (!displayName) {
    return '?';
  }

  const parts = displayName
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return (
    parts[0][0] + (parts.at(-1)?.[0] ?? '')
  ).toUpperCase();
}
